import { Directive, EffectRef, Input, Signal, effect } from '@angular/core';
import { ApiSignal, apiErrorSymbol } from '../util/api-signal';
import { NotificationUtils } from '../util/notification';

/**
 * A helper directive for placing a loading sd-spinner.
 */
@Directive({
    standalone: false
})
export abstract class LoadingIndicatorDirectiveBase {
  /** Main api signal for loading and error detection. */
  @Input({ required: false }) public apiSignal?: ApiSignal<unknown> | null = null;

  /** displayed message if an error occurs. If `null` or `undefined` the message of the occured error will be displayed. */
  @Input({ required: false }) public apiCallErrorMsg: string | null | undefined;
  /** If true spinner will only be displayed once. */
  @Input({ required: false }) public apiCallOnce: boolean = false;

  /** If true a error label will be displayed when an error occurs. */
  @Input({ required: false }) public apiCallDisplayErrorElement: boolean = true;
  /** If true a toast will be emitted if an error occurs. The message depends on {@link apiCallErrorMsg}. */
  @Input({ required: false }) public apiCallDisplayErrorToast: boolean = true;

  /** Binds an additional signal to the directive. If this signal holds `true`, the spinner will be displayed also. */
  @Input({ required: false }) public apiCallAdditionalLoading: Signal<boolean> | null = null;

  private readonly updateEffect: EffectRef;
  private currentlyPlaced: null | Error | 'loading' | 'regular' = null;

  constructor() {
    this.updateEffect = effect(() => {
      const apiSignalLoading: boolean = !!this.apiSignal && this.apiSignal() === undefined;
      const loading: boolean = !!(apiSignalLoading || this.apiCallAdditionalLoading?.());
      const error: Error | undefined = this.apiSignal ? this.apiSignal[apiErrorSymbol]() : undefined;

      this.handleSignalUpdate(loading, error);
    });
  }

  private readonly handleSignalUpdate = (loading: boolean, error: Error | undefined): void => {
    if (error) {
      if (error === this.currentlyPlaced) {
        return;
      }

      this.clear();
      this.currentlyPlaced = error;

      this.placeError(error);

      if (this.apiCallDisplayErrorToast) {
        NotificationUtils.displayNotification(this.apiCallErrorMsg || error.message, 'error', { closable: true, durationInMs: 5000 });
      }

      return;
    }

    if (loading) {
      if (this.currentlyPlaced === 'loading') {
        return;
      }

      this.clear();
      this.currentlyPlaced = 'loading';

      this.placeLoading();
      return;
    }

    if (this.currentlyPlaced === 'regular') {
      return;
    }

    this.clear();
    this.currentlyPlaced = 'regular';

    this.placeRegular();

    if (this.apiCallOnce) {
      this.updateEffect.destroy();
    }
  };

  protected abstract placeRegular(): void;

  protected abstract placeError(error: Error): void;

  protected abstract placeLoading(): void;

  protected abstract clear(): void;
}
